import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ListMusic, Loader2, Pencil, Play, Plus, Trash2, X } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Cover,
  DragHandle,
  DropLine,
  IconButton,
  SongSearch,
  SpotifyBadge,
  formatTime,
  useDragOrder,
  type SearchResult,
  type Song,
} from '@/components/music';

/**
 * Personal playlists on the Music page (web-api routes/playlists.ts, also
 * `/playlist` in Discord). Own ones can be edited with the Music view right;
 * ones others shared on this server can only be played. Playing needs the edit
 * right (the parent passes canPlay).
 */

/** Song as stored in a playlist. */
export interface PlaylistSong {
  id: string;
  title: string;
  artist: string | null;
  url: string;
  durationSec: number | null;
  thumbnail: string | null;
  source: 'youtube' | 'soundcloud' | 'spotify';
  spotify?: { title: string; artists?: string | null; durationSec?: number | null } | null;
}
type SongInput = Omit<PlaylistSong, 'id'>;

interface PlaylistSummary {
  id: string;
  name: string;
  ownerId: string;
  ownerName: string;
  own: boolean;
  shared: boolean;
  songCount: number;
  durationSec: number;
  cover: string | null;
}

/** A song of the queue (Spotify ones keep what finds them on YouTube). */
export function fromQueueSong(s: Song): SongInput {
  const source = s.source ?? 'youtube';
  return {
    title: s.title,
    artist: s.channel,
    url: s.url,
    durationSec: s.durationSec,
    thumbnail: s.thumbnail,
    source,
    spotify: source === 'spotify' ? { title: s.title, artists: s.channel, durationSec: s.durationSec } : null,
  };
}

function fromSearchResult(r: SearchResult): SongInput {
  return { title: r.title, artist: r.channel, url: r.url, durationSec: r.duration, thumbnail: r.thumbnail, source: r.source };
}

const listKey = (guildId: string) => ['playlists', guildId];
const detailKey = (guildId: string, id: string) => ['playlist', guildId, id];
const base = (guildId: string) => `/api/guilds/${guildId}/playlists`;
const songCount = (n: number) => `${n} ${n === 1 ? 'Song' : 'Songs'}`;

function usePlaylists(guildId: string) {
  return useQuery({
    queryKey: listKey(guildId),
    queryFn: () => api<{ playlists: PlaylistSummary[] }>(base(guildId)),
  });
}

/** Reloads list + detail after a change. */
function useRefresh(guildId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: listKey(guildId) });
    qc.invalidateQueries({ queryKey: ['playlist', guildId] });
  };
}

const post = (url: string, body: object, method = 'POST') => api(url, { method, body: JSON.stringify(body) });

export function PlaylistsTab({
  guildId,
  canPlay,
  playHint,
  playing,
  onPlay,
}: {
  guildId: string;
  canPlay: boolean;
  /** Why ▶ is off, as tooltip (no edit right / no voice channel). */
  playHint: string | null;
  playing: boolean;
  onPlay: (playlistId: string) => void;
}) {
  const list = usePlaylists(guildId);
  const playlists = list.data?.playlists ?? [];
  const [selected, setSelected] = useState<string | null>(null);
  useEffect(() => {
    if (!selected && playlists[0]) setSelected(playlists[0].id);
    // Not while refetching: a just-created playlist isn't in the old list yet.
    if (selected && list.data && !list.isFetching && !playlists.some((p) => p.id === selected)) {
      setSelected(playlists[0]?.id ?? null);
    }
  }, [list.data, list.isFetching, playlists, selected]);

  // Phones: the opened playlist sits below the list — scroll there on a tap.
  const detailRef = useRef<HTMLDivElement>(null);
  const scrollToDetail = useRef(false);
  const open = (id: string) => {
    scrollToDetail.current = window.matchMedia('(max-width: 1023px)').matches;
    setSelected(id);
  };
  useEffect(() => {
    if (!scrollToDetail.current) return;
    scrollToDetail.current = false;
    detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [selected]);

  const own = playlists.filter((p) => p.own);
  const shared = playlists.filter((p) => !p.own);

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,260px)_minmax(0,1fr)]">
      <div className="space-y-4">
        <NewPlaylist guildId={guildId} onCreated={open} />
        {list.isLoading && <p className="text-sm text-muted-foreground">Lade Playlists…</p>}
        {list.error && <p className="text-sm text-destructive">{(list.error as Error).message}</p>}
        <PlaylistGroup title="Meine Playlists" items={own} selected={selected} onSelect={open} empty="Noch keine — leg oben eine an." />
        {shared.length > 0 && (
          <PlaylistGroup title="Auf diesem Server geteilt" items={shared} selected={selected} onSelect={open} />
        )}
      </div>
      {selected ? (
        <div ref={detailRef} className="scroll-mt-4">
          <PlaylistDetail
            key={selected}
            guildId={guildId}
            id={selected}
            canPlay={canPlay}
            playHint={playHint}
            playing={playing}
            onPlay={onPlay}
          />
        </div>
      ) : (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            Playlists gehören dir und gehen auf jedem Server mit dem Bot. Auf Wunsch siehst du sie auch hier mit den
            anderen — zum Abspielen, nicht zum Ändern. In Discord: <code>/playlist</code>.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function PlaylistGroup({
  title,
  items,
  selected,
  onSelect,
  empty,
}: {
  title: string;
  items: PlaylistSummary[];
  selected: string | null;
  onSelect: (id: string) => void;
  empty?: string;
}) {
  return (
    <div>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</div>
      {items.length === 0 && empty ? (
        <p className="text-sm text-muted-foreground">{empty}</p>
      ) : (
        <ul className="space-y-1">
          {items.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => onSelect(p.id)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg border p-2 text-left transition-colors',
                  selected === p.id ? 'border-primary bg-primary/10' : 'bg-card hover:border-primary/60'
                )}
              >
                <Cover src={p.cover} className="h-10 w-10" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{p.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {songCount(p.songCount)}
                    {!p.own && ` · von ${p.ownerName}`}
                    {p.own && p.shared && ' · geteilt'}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** "Neue Playlist": a name, or a Spotify / YouTube playlist link to take over. */
function NewPlaylist({ guildId, onCreated }: { guildId: string; onCreated: (id: string) => void }) {
  const refresh = useRefresh(guildId);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const create = useMutation({
    mutationFn: () =>
      post(base(guildId), { ...(name.trim() ? { name: name.trim() } : {}), ...(url.trim() ? { url: url.trim() } : {}) }) as Promise<{
        playlist: PlaylistSummary;
      }>,
    onSuccess: (res) => {
      refresh();
      onCreated(res.playlist.id);
      setName('');
      setUrl('');
      setOpen(false);
    },
  });

  if (!open) {
    return (
      <Button variant="outline" className="w-full gap-2" onClick={() => setOpen(true)}>
        <Plus className="h-4 w-4" /> Neue Playlist
      </Button>
    );
  }
  return (
    <Card>
      <CardContent className="pt-4 max-md:pt-4">
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!create.isPending && (name.trim() || url.trim())) create.mutate();
          }}
        >
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={50} placeholder="Name" autoFocus />
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="optional: Spotify- oder YouTube-Playlist-Link"
          />
          <p className="text-[11px] text-muted-foreground">
            Mit Link werden bis zu 100 Songs übernommen — danach gehört die Playlist dir. Ohne Namen heißt sie wie das
            Original.
          </p>
          {create.error && <p className="text-xs text-destructive">{(create.error as Error).message}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Abbrechen
            </Button>
            <Button type="submit" size="sm" disabled={create.isPending || (!name.trim() && !url.trim())}>
              {create.isPending ? (url.trim() ? 'Lese Link…' : 'Erstelle…') : 'Erstellen'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function PlaylistDetail({
  guildId,
  id,
  canPlay,
  playHint,
  playing,
  onPlay,
}: {
  guildId: string;
  id: string;
  canPlay: boolean;
  playHint: string | null;
  playing: boolean;
  onPlay: (playlistId: string) => void;
}) {
  const refresh = useRefresh(guildId);
  const detail = useQuery({
    queryKey: detailKey(guildId, id),
    queryFn: () => api<{ playlist: PlaylistSummary & { songs: PlaylistSong[] } }>(`${base(guildId)}/${id}`),
  });
  const p = detail.data?.playlist;
  const url = `${base(guildId)}/${id}`;

  const patch = useMutation({ mutationFn: (body: { name?: string; shared?: boolean }) => post(url, body, 'PATCH'), onSuccess: refresh });
  const remove = useMutation({ mutationFn: () => api(url, { method: 'DELETE' }), onSuccess: refresh });
  const addSongs = useMutation({ mutationFn: (songs: SongInput[]) => post(`${url}/songs`, { songs }), onSuccess: refresh });
  const reorder = useMutation({ mutationFn: (ids: string[]) => post(`${url}/songs`, { ids }, 'PUT'), onSuccess: refresh });
  const importLink = useMutation({
    mutationFn: (link: string) => post(`${url}/import`, { url: link }) as Promise<{ added: number; truncated: boolean }>,
    onSuccess: refresh,
  });

  const [renaming, setRenaming] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Shown until the saved order comes back.
  const [optimistic, setOptimistic] = useState<string[] | null>(null);
  useEffect(() => setOptimistic(null), [detail.data]);

  const songs = p?.songs ?? [];
  const byId = new Map(songs.map((s) => [s.id, s]));
  const shown = optimistic ? optimistic.map((sid) => byId.get(sid)).filter((s): s is PlaylistSong => !!s) : songs;
  const saveOrder = (ids: string[]) => {
    setOptimistic(ids);
    reorder.mutate(ids);
  };
  const drag = useDragOrder(shown.length, (from, to) => {
    const ids = shown.map((s) => s.id);
    ids.splice(to, 0, ...ids.splice(from, 1));
    saveOrder(ids);
  });

  if (detail.isLoading) return <p className="text-sm text-muted-foreground">Lade…</p>;
  if (detail.error || !p) return <p className="text-sm text-destructive">{(detail.error as Error | null)?.message ?? 'Nicht gefunden.'}</p>;

  const error = [patch, remove, addSongs, reorder, importLink].find((m) => m.error)?.error as Error | undefined;

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap items-center gap-4">
            <Cover src={p.cover} className="h-16 w-16 sm:h-20 sm:w-20" />
            {/* min-w: narrow screens push the play button to its own line. */}
            <div className="min-w-[9rem] flex-1">
              {renaming !== null ? (
                <div className="flex items-center gap-2">
                  <Input value={renaming} onChange={(e) => setRenaming(e.target.value)} maxLength={50} autoFocus className="h-9" />
                  <IconButton
                    label="Speichern"
                    disabled={!renaming.trim() || patch.isPending}
                    onClick={() => patch.mutate({ name: renaming.trim() }, { onSuccess: () => setRenaming(null) })}
                  >
                    <Check className="h-4 w-4" />
                  </IconButton>
                  <IconButton label="Abbrechen" onClick={() => setRenaming(null)}>
                    <X className="h-4 w-4" />
                  </IconButton>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <h2 className="min-w-0 truncate text-xl font-semibold">{p.name}</h2>
                  {p.own && (
                    <IconButton label="Umbenennen" className="h-8 w-8" onClick={() => setRenaming(p.name)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </IconButton>
                  )}
                </div>
              )}
              <div className="text-sm text-muted-foreground">
                {songCount(p.songCount)}
                {p.durationSec > 0 && ` · ${formatTime(p.durationSec)}`}
                {!p.own && ` · von ${p.ownerName}`}
              </div>
            </div>
            <Button
              className="gap-2 max-sm:w-full"
              disabled={!canPlay || playing || p.songCount === 0}
              title={playHint ?? undefined}
              onClick={() => onPlay(p.id)}
            >
              {playing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4 fill-current" />} Abspielen
            </Button>
          </div>
          {playHint && <p className="text-xs text-muted-foreground">{playHint}</p>}

          {p.own && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3">
              <Switch
                checked={p.shared}
                disabled={patch.isPending}
                onChange={(e) => patch.mutate({ shared: e.target.checked })}
                label="Für Leute auf diesem Server sichtbar (zum Abspielen)"
              />
              {confirmDelete ? (
                <span className="flex items-center gap-2 text-sm">
                  Wirklich löschen?
                  <Button variant="destructive" size="sm" disabled={remove.isPending} onClick={() => remove.mutate()}>
                    Löschen
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>
                    Nein
                  </Button>
                </span>
              ) : (
                <Button variant="ghost" size="sm" className="gap-1.5 text-destructive" onClick={() => setConfirmDelete(true)}>
                  <Trash2 className="h-4 w-4" /> Playlist löschen
                </Button>
              )}
            </div>
          )}
          {error && <p className="text-sm text-destructive">{error.message}</p>}
        </CardContent>
      </Card>

      {p.own && (
        <div className="relative z-20 space-y-1">
          <SongSearch
            guildId={guildId}
            canPlay
            busy={addSongs.isPending || importLink.isPending}
            onPickResult={(r) => addSongs.mutate([fromSearchResult(r)])}
            onPlay={(link) => importLink.mutate(link)}
            placeholder="Song suchen und hinzufügen — oder Playlist-Link einfügen"
            linkHint="übernimmt die Songs des Links (Spotify oder YouTube, bis 100) hinten in diese Playlist."
          />
          {importLink.isPending && <p className="text-xs text-muted-foreground">Lese den Link…</p>}
          {importLink.data && !importLink.isPending && (
            <p className="text-xs text-primary">
              {songCount(importLink.data.added)} übernommen{importLink.data.truncated && ' (mehr passten nicht)'}.
            </p>
          )}
        </div>
      )}

      {shown.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            {p.own ? 'Noch leer — oben suchen oder einen Link einfügen.' : 'Diese Playlist ist leer.'}
          </CardContent>
        </Card>
      ) : (
        <ul ref={drag.listRef} className="divide-y rounded-lg border bg-card">
          {shown.map((s, i) => {
            const line = p.own ? drag.lineAt(i) : null;
            return (
              <li
                key={s.id}
                className={cn('relative flex items-center gap-2 p-2 text-sm sm:gap-3', drag.draggingIndex === i && 'bg-accent/60 opacity-60')}
              >
                {line && <DropLine position={line} />}
                {p.own && <DragHandle {...drag.handle(i)} disabled={reorder.isPending} />}
                <span className="hidden w-6 shrink-0 text-center font-mono text-xs text-muted-foreground sm:inline">{i + 1}</span>
                <Cover src={s.thumbnail} className="h-9 w-12 sm:h-10 sm:w-16" />
                <div className="min-w-0 flex-1">
                  <a href={s.url} target="_blank" rel="noreferrer" className="block truncate hover:text-primary" title={s.title}>
                    {s.title}
                  </a>
                  <div className="truncate text-xs text-muted-foreground">
                    {s.source === 'spotify' && <SpotifyBadge />}
                    {s.artist}
                  </div>
                </div>
                {s.durationSec != null && (
                  <span className="hidden shrink-0 font-mono text-xs text-muted-foreground sm:inline">{formatTime(s.durationSec)}</span>
                )}
                {p.own && (
                  <IconButton
                    label="Aus der Playlist entfernen"
                    className="h-8 w-8 hover:text-destructive"
                    disabled={reorder.isPending}
                    onClick={() => saveOrder(shown.filter((x) => x.id !== s.id).map((x) => x.id))}
                  >
                    <X className="h-4 w-4" />
                  </IconButton>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** "Zur Playlist hinzufügen" from the queue / now playing: pick one of your playlists or make a new one. */
export function AddToPlaylistDialog({ guildId, song, onClose }: { guildId: string; song: SongInput; onClose: () => void }) {
  const list = usePlaylists(guildId);
  const refresh = useRefresh(guildId);
  const own = (list.data?.playlists ?? []).filter((p) => p.own);
  const [newName, setNewName] = useState('');
  const [doneIn, setDoneIn] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: async (target: { id?: string; name?: string }) => {
      let id = target.id;
      let name = target.name ?? '';
      if (!id) {
        const res = (await post(base(guildId), { name })) as { playlist: PlaylistSummary };
        id = res.playlist.id;
        name = res.playlist.name;
      }
      await post(`${base(guildId)}/${id}/songs`, { songs: [song] });
      return name;
    },
    onSuccess: (name) => {
      refresh();
      setDoneIn(name);
      setTimeout(onClose, 900);
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="glass-float w-full max-w-sm overflow-hidden rounded-lg border bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b p-4">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Zur Playlist hinzufügen</div>
            <div className="truncate font-medium">{song.title}</div>
          </div>
          <IconButton label="Schließen" className="h-8 w-8" onClick={onClose}>
            <X className="h-4 w-4" />
          </IconButton>
        </div>
        <div className="max-h-72 space-y-1 overflow-y-auto p-2">
          {doneIn && (
            <p className="flex items-center gap-2 p-2 text-sm text-primary">
              <Check className="h-4 w-4" /> Zu „{doneIn}“ hinzugefügt.
            </p>
          )}
          {!doneIn && list.isLoading && <p className="p-2 text-sm text-muted-foreground">Lade…</p>}
          {!doneIn &&
            own.map((p) => (
              <button
                key={p.id}
                type="button"
                disabled={add.isPending}
                onClick={() => add.mutate({ id: p.id, name: p.name })}
                className="flex w-full items-center gap-3 rounded-md p-2 text-left text-sm hover:bg-accent disabled:opacity-50"
              >
                <ListMusic className="h-4 w-4 shrink-0 text-primary" />
                <span className="min-w-0 flex-1 truncate">{p.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{p.songCount}</span>
              </button>
            ))}
          {!doneIn && list.data && own.length === 0 && <p className="p-2 text-sm text-muted-foreground">Du hast noch keine Playlist.</p>}
        </div>
        {!doneIn && (
          <div className="flex gap-2 border-t p-3">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} maxLength={50} placeholder="Neue Playlist" className="h-9" />
            <Button size="sm" disabled={!newName.trim() || add.isPending} onClick={() => add.mutate({ name: newName.trim() })} className="gap-1">
              <Plus className="h-4 w-4" /> Anlegen
            </Button>
          </div>
        )}
        {add.error && <p className="px-4 pb-3 text-xs text-destructive">{(add.error as Error).message}</p>}
      </div>
    </div>
  );
}

